import { useEffect } from "react";
import { useParams } from "react-router-dom";
import "../License/License.css";
import "../../Dashboard/Dashboard.css";
import "../../../../../common/styles/table.css";
import BackButton from "../../../../common/BackButton";
import { useGetPaymentById } from "../../../../../hooks/school/payments/useGetPayment";
import ViewPaymentSkeleton from "../../../common/ViewPaymentSkeleton";

/**
 * View Payment Component
 */
const ViewPayment = () => {
  const { paymentId } = useParams();

  const { payment, loading, error, getPaymentById } = useGetPaymentById();

  useEffect(() => {
    if (paymentId) {
      getPaymentById(paymentId);
    }
  }, [paymentId]);

  const isApproved = payment?.status === "paid";
  const isRejected = payment?.status === "failed";

  return (
    <section>
      <div className="container-fluid">
        <div className="row mb-4 align-items-center">
          <div className="col-12">
            <BackButton />
          </div>

          <div className="col-md-6 my-4">
            <p className="tchrdashboard-cmntitl mb-0">VIEW PAYMENT</p>
          </div>
        </div>

        {loading ? (
          <ViewPaymentSkeleton />
        ) : error ? (
          <div className="text-danger text-center">
            Something Went Wrong. {error}
          </div>
        ) : (
          <div className="row">
            <hr />

            <div className="d-flex justify-content-end mt-3">
              {isApproved && (
                <span className="badge bg-success px-4 py-2 rounded-pill">
                  <i className="fa fa-check me-2"></i> Approved
                </span>
              )}

              {isRejected && (
                <span className="badge bg-danger px-4 py-2 rounded-pill">
                  <i className="fa fa-times me-2"></i> Rejected
                </span>
              )}
            </div>

            {/* ===== Payment Details ===== */}
            <div className="col-lg-6 mb-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white fw-bold">
                  Payment Details
                </div>

                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Amount</span>
                    <h5 className="mb-0 text-success">{payment?.amount} ({payment?.currency})</h5>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Status</span>
                    <span
                      className={`badge px-3 py-2 rounded-pill ${payment?.status === "paid"
                        ? "bg-success"
                        : payment?.status === "failed"
                          ? "bg-danger"
                          : "bg-warning text-dark"
                        }`}
                    >
                      {payment?.status}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Payment Method</span>
                    <span>
                      {payment?.method
                        ?.split("_")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(" ")}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Source</span>
                    <span>
                      {payment?.source?.charAt(0).toUpperCase() +
                        payment?.source?.slice(1)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Reference ID</span>
                    <span className="fw-semibold">
                      {payment?.reference_id || "-"}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Verified</span>
                    {payment?.is_verified ? (
                      <span className="badge bg-success rounded-pill px-3 py-2">
                        Verified
                      </span>
                    ) : (
                      <span className="badge bg-danger rounded-pill px-3 py-2">
                        Not Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Invoice Details ===== */}
            <div className="col-lg-6 mb-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white fw-bold">
                  Invoice Details
                </div>

                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Invoice No</span>
                    <span className="fw-semibold">
                      {payment?.invoice?.invoice_no}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Total Amount</span>
                    <span>
                      {Number(payment?.invoice?.total_amount).toLocaleString(
                        "en-IN"
                      )} ({payment?.currency})
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Paid Amount</span>
                    <span>
                      {Number(payment?.invoice?.paid_amount).toLocaleString(
                        "en-IN"
                      )} ({payment?.currency})
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Invoice Status</span>
                    <span className="badge bg-info rounded-pill px-3 py-2">
                      {payment?.invoice?.status}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Issued At</span>
                    <span>
                      {new Date(payment?.invoice?.issued_at).toLocaleDateString(
                        "en-IN"
                      )}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Due Date</span>
                    <span className="text-danger fw-semibold">
                      {new Date(payment?.invoice?.due_at).toLocaleDateString(
                        "en-IN"
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Proof Images ===== */}
            {payment?.proofs?.length > 0 && (
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-header bg-white fw-bold">
                    Payment Proofs
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {payment.proofs.map((p) => (
                        <div className="col-md-3 col-sm-6" key={p.id}>
                          <a href={p.url} target="_blank" rel="noreferrer">
                            <img
                              src={p.url}
                              alt="proof"
                              className="img-fluid rounded shadow-sm"
                              style={{
                                height: "150px",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ViewPayment;
